import { Suspense, lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import PRIVILEGES from '../constants/privileges';
import ErrorPage from '../modules/error/ErrorPage';
import LogisticsCreate from '../modules/project/components/pages/logistics/LogisticsCreate';
import SkeletonPage from '../modules/skeleton/SkeletonPage';
import { RBACRoute } from './RBACRoute';

const Project = lazy(() => import('../modules/project'));
const Pages = lazy(() => import('../modules/project/components/pages'));
const CreateProject = lazy(
  () => import('../modules/projects/components/create-project')
);
const CreateProjectRoute = lazy(
  () =>
    import('../modules/projects/components/create-project/CreateProjectRoute')
);
const TaskSelectionPage = lazy(
  () =>
    import(
      '../modules/project/components/pages/payment-tracker/TaskSelectionPage'
    )
);
const TaskDetailsPage = lazy(
  () => import('../modules/project/components/pages/tasks/TaskDetailsPage')
);
const MaterialsIndentDetails = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialsIndentDetails'
    )
);
const MaterialIndentClone = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialIndentClone'
    )
);
const MaterialindentEdit = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialindentEdit'
    )
);
const MaterialIndentCreate = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialIndentCreate'
    )
);
const MaterialIndentDispatch = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialIndentDispatch'
    )
);
const MaterialIndentDispatchPO = lazy(
  () =>
    import(
      '../modules/project/components/pages/material-indent/MaterialIndentDispatchPO'
    )
);
const PurchaseOrderDetails = lazy(
  () =>
    import(
      '../modules/project/components/pages/purchase-orders/PurchaseOrderDetails'
    )
);
const PurchaseOrderClone = lazy(
  () =>
    import(
      '../modules/project/components/pages/purchase-orders/PurchaseOrderClone'
    )
);
const PurchaseOrderEdit = lazy(
  () =>
    import(
      '../modules/project/components/pages/purchase-orders/PurchaseOrderEdit'
    )
);
const PurchaseOrderCreate = lazy(
  () =>
    import(
      '../modules/project/components/pages/purchase-orders/PurchaseOrderCreate'
    )
);
const LogisticsDetails = lazy(
  () => import('../modules/project/components/pages/logistics/LogisticsDetails')
);
const LogisticsEdit = lazy(
  () => import('../modules/project/components/pages/logistics/LogisticsEdit')
);
const MomCreate = lazy(
  () => import('../modules/project/components/pages/mom/MomCreate')
);
const MomDetails = lazy(
  () => import('../modules/project/components/pages/mom/MomDetails')
);
const MomEdit = lazy(
  () => import('../modules/project/components/pages/mom/MomEdit')
);

const ProjectRoutes = (
  <>
    <Route
      path="/project/:pID"
      element={
        <Suspense fallback={<SkeletonPage />}>
          <Project />
        </Suspense>
      }
      errorElement={<ErrorPage />}
    >
      <Route
        path=":page"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <Pages />
          </Suspense>
        }
      >
        <Route
          path=":subPage"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Pages />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="boq/:boqLevel"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_BOQ_VIEW]}>
            <Suspense fallback={<SkeletonPage />}>
              <Pages />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="tasks/:taskPage/:taskId/:taskManagerPage"
        element={
          <RBACRoute
            privileges={[PRIVILEGES.PROJECT.PROJECT_TASK_DETAILS_VIEW]}
          >
            <Suspense fallback={<SkeletonPage />}>
              <TaskDetailsPage />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="material-indent/:mId/view"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_MI_DETAILS_VIEW]}>
            <Suspense fallback={<SkeletonPage />}>
              <MaterialsIndentDetails />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="material-indent/:mId/clone"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_MI_CLONE]}>
            <Suspense fallback={<SkeletonPage />}>
              <MaterialIndentClone />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="material-indent/:mId/edit"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_MI_UPDATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <MaterialindentEdit />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="material-indent/:mId/dispatch"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <MaterialIndentDispatch />
          </Suspense>
        }
      />
      <Route
        path="material-indent/:mId/dispatch-po"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <MaterialIndentDispatchPO />
          </Suspense>
        }
      />
      <Route
        path="material-indent/new/create"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_MI_CREATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <MaterialIndentCreate />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="purchase-orders/:poId/view"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_PO_DETAILS_VIEW]}>
            <Suspense fallback={<SkeletonPage />}>
              <PurchaseOrderDetails />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="purchase-orders/:poId/clone"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_PO_CLONE]}>
            <Suspense fallback={<SkeletonPage />}>
              <PurchaseOrderClone />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="purchase-orders/:poId/edit"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_PO_UPDATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <PurchaseOrderEdit />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="purchase-orders/new/create"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_PO_CREATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <PurchaseOrderCreate />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="logistics/:id/view"
        element={
          <RBACRoute
            privileges={[PRIVILEGES.PROJECT.PROJECT_LOGISTICS_DETAILS_VIEW]}
          >
            <Suspense fallback={<SkeletonPage />}>
              <LogisticsDetails />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="logistics/:id/edit"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_LOGISTICS_UPDATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <LogisticsEdit />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="logistics/new"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_LOGISTICS_CREATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <LogisticsCreate />
            </Suspense>
          </RBACRoute>
        }
      />
      <Route
        path="mom/new"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <MomCreate />
          </Suspense>
        }
      />
      <Route
        path="mom/:meetingId/view"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <MomDetails />
          </Suspense>
        }
      />
      <Route
        path="mom/:meetingId/edit"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <MomEdit />
          </Suspense>
        }
      />
      <Route
        path="add-tasks/:installmentId"
        element={
          <RBACRoute
            privileges={[PRIVILEGES.PROJECT.PROJECT_PAYMENT_TRACKER_ADD_TASK]}
          >
            <Suspense fallback={<SkeletonPage />}>
              <TaskSelectionPage />
            </Suspense>
          </RBACRoute>
        }
      />
    </Route>
    <Route path="/projects" errorElement={<ErrorPage />}>
      <Route
        path="create"
        element={
          <RBACRoute privileges={[PRIVILEGES.PROJECT.PROJECT_CREATE]}>
            <Suspense fallback={<SkeletonPage />}>
              <CreateProject />
            </Suspense>
          </RBACRoute>
        }
      >
        <Route index element={<Navigate to="project-details" replace />} />
        <Route
          path=":projectPage"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CreateProjectRoute />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="edit/:pID"
        element={
          <RBACRoute
            privileges={[
              PRIVILEGES.PROJECT.PROJECT_CREATE,
              PRIVILEGES.PROJECT.PROJECT_UPDATE,
            ]}
          >
            <Suspense fallback={<SkeletonPage />}>
              <CreateProject />
            </Suspense>
          </RBACRoute>
        }
      >
        <Route index element={<Navigate to="project-details" replace />} />
        <Route
          path=":projectPage"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CreateProjectRoute />
            </Suspense>
          }
        />
      </Route>
    </Route>
  </>
);

export default ProjectRoutes;
